import { Check, Maximize2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { formatJSON, isValidJSON } from "../utils";

import { Variable } from "@/components/InputVariableConfigurator/types";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { isModifierKeyPressed, SHORTCUTS } from "@/utils/shortcuts";

interface MiniEditorProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onExpand: () => void;
  variable: Variable;
  onChange: (variable: Variable) => void;
  children: React.ReactNode; // The trigger element (input field)
  readOnly?: boolean;
  onFocusNext?: () => void;
  popoverPortalContainer?: HTMLElement | null;
}

const MiniEditor = ({
  isOpen,
  onOpenChange,
  onExpand,
  variable,
  onChange,
  children,
  readOnly = false,
  onFocusNext,
  popoverPortalContainer,
}: MiniEditorProps) => {
  const [localValue, setLocalValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initialize local value when opening
  useEffect(() => {
    if (isOpen) {
      const displayValue =
        typeof variable.value === "object"
          ? JSON.stringify(variable.value, null, 2)
          : String(variable.value);
      setLocalValue(displayValue);

      // Focus textarea after a brief delay to ensure it's rendered
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.select();
      }, 50);
    }
  }, [isOpen, variable.value]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value);
  };

  const handleSave = () => {
    let valueToSave = localValue;

    // Try to parse and format JSON
    if (isValidJSON(localValue)) {
      valueToSave = formatJSON(localValue);
    }

    // Commit to parent
    onChange({ ...variable, value: valueToSave });
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset local value to original and close without saving
    const displayValue =
      typeof variable.value === "object"
        ? JSON.stringify(variable.value, null, 2)
        : String(variable.value);
    setLocalValue(displayValue);
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      handleCancel();
    } else if (isModifierKeyPressed(e) && e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleSave();
    } else if (e.key === "Tab" && !e.shiftKey && onFocusNext) {
      e.preventDefault();
      e.stopPropagation();
      handleSave();
      // Focus next input after a brief delay to ensure save completes
      setTimeout(() => {
        onFocusNext();
      }, 50);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        portalContainer={popoverPortalContainer}
        className="-ml-[0.5px] w-[min(90vw,600px)] p-0"
        align="start"
        side="bottom"
        sideOffset={4}
        onOpenAutoFocus={e => {
          // Prevent auto-focus on content, we'll handle it manually
          e.preventDefault();
        }}
        onInteractOutside={() => {
          // Save when clicking outside
          handleSave();
        }}
        onWheel={e => {
          e.stopPropagation();
        }}
      >
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">{variable.key || "Edit Value"}</span>
          <div className="flex items-center gap-1">
            <BasicTooltip content="Expand editor">
              <Button variant="ghost" size="tinyIcon" onClick={onExpand} tabIndex={-1}>
                <Maximize2 className="h-4 w-4" />
              </Button>
            </BasicTooltip>
            <BasicTooltip content={`Save (${SHORTCUTS.SUBMIT.displayWithPlus})`}>
              <Button variant="ghost" size="tinyIcon" onClick={handleSave} tabIndex={-1}>
                <Check className="h-4 w-4" />
              </Button>
            </BasicTooltip>
            <BasicTooltip content="Cancel (Esc)">
              <Button variant="ghost" size="tinyIcon" onClick={handleCancel} tabIndex={-1}>
                <X className="h-4 w-4" />
              </Button>
            </BasicTooltip>
          </div>
        </div>
        <div className="p-2">
          <Textarea
            ref={textareaRef}
            placeholder="Enter value..."
            readOnly={readOnly}
            className="max-h-[300px] min-h-[120px] w-full resize-y font-mono text-sm"
            value={localValue}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className="border-t px-3 py-2">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Press {SHORTCUTS.SUBMIT.displayWithPlus} to save, Esc to cancel</span>
            <button className="text-blue-600 hover:underline" onClick={onExpand}>
              Open fullscreen editor
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MiniEditor;
